import React, { useEffect, useState } from "react"
import { useTranslation } from "next-i18next"
import { VerifyEmailAddressModal } from "./VerifyEmailAddressModal"
import { AddPhotoSvg, CheckCircleSvg, DiscordSvg, EmailSvg, Person1Svg, TwitterSvg } from "../../assets/svg"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { useForm } from "react-hook-form"
import clsx from "clsx"
import { TelegramUser } from "@nixjs23n6/telegram-login"
import { ConfirmUpdateModal } from "./ConfirmUpdateModal"
import useGlobalStore from "../../hooks/useGlobalStore"
import { CancelConfirmModal } from "./CancelConfirmModal"
import {
    getDiscordUrl,
    getEmailVerification,
    getTwitterUrl,
    updateProfile,
    verifyTelegram
} from "../../services/account"
import { useMatchMutate } from "../../hooks/useMatchMutate"

interface EditAccountProps {
    onCancel: () => void
    profile: any
}

export const EditAccount = ({ profile, onCancel }: EditAccountProps) => {
    const { t: tCommon } = useTranslation("common")
    const { t: tMyAccount } = useTranslation("my-account")

    const { name, email, profileImage, discord, twitter, telegram } = profile

    const mutate = useMatchMutate()

    const { isLoggedIn, setIsOpenLoginModal, setPopupMessage } = useGlobalStore()
    const [isOpenVerifyAddressModal, setIsOpenVerifyAddressModal] = useState(false)
    const [isOpenConfirmUpdateModal, setIsOpenConfirmUpdateModal] = useState(false)
    const [isOpenCancelConfirmUpdateModal, setIsOpenCancelConfirmUpdateModal] = useState(false)

    const schema = yup.object().shape({
        photo: yup.mixed().default(null),
        userName: yup.string().required().max(256),
        email: yup.string().email().max(256).default("")
    })

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        setValue,
        watch
    } = useForm({
        defaultValues: {
            photo: null,
            userName: name || "",
            email: email || ""
        },
        resolver: yupResolver(schema)
    })

    const watchPhoto = watch("photo")
    const watchEmail = watch("email")

    const [preview, setPreview] = useState("")
    const onSelectFile = (e) => {
        if (!e.target.files || e.target.files.length === 0 || !e.target.files[0].name.match(/\.(jpg|jpeg|png)$/i)) {
            setValue("photo", undefined)

            return
        }

        setValue("photo", e.target.files[0])
    }

    useEffect(() => {
        if (!watchPhoto) {
            setPreview(undefined)

            return
        }

        const objectUrl = URL.createObjectURL(watchPhoto)
        setPreview(objectUrl)

        return () => URL.revokeObjectURL(objectUrl)
    }, [watchPhoto])

    const [canResendEmailOtpAfter, setCanResendEmailOtpAfter] = useState(0)
    const onVerifyEmail = async () => {
        if (!watchEmail) {
            return
        }

        setCanResendEmailOtpAfter(0)

        try {
            const response = await getEmailVerification(watchEmail)

            setCanResendEmailOtpAfter(response.data.data.otpResendSecondLeft)
        } catch (ex) {
            setPopupMessage({ message: ex.response ? ex.response.data.message : ex })
        }
    }

    const [isEmailVerified, setIsEmailVerified] = useState(false)

    const onVerifyEmailSuccess = async () => {
        setIsEmailVerified(true)

        await mutate(/MY_PROFILE/)
    }

    useEffect(() => {
        if (canResendEmailOtpAfter > 0) {
            setIsOpenVerifyAddressModal(true)
        }
    }, [canResendEmailOtpAfter])

    const openWindow = (url, title) => {
        let top = window.screen.height - 300
        top = top > 0 ? top / 2 : 0

        let left = window.screen.width - 400
        left = left > 0 ? left / 2 : 0

        const newWindow = window.open(
            url,
            title,
            `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no,width=600,height=700,top=${top},left=${left}`
        )
        newWindow.moveTo(left, top)
        newWindow.focus()

        return newWindow
    }

    const [isDiscordVerified, setIsDiscordVerified] = useState(false)

    const onVerifyDiscord = async () => {
        if (typeof window === "undefined") {
            return
        }

        setDiscordError("")

        const response = await getDiscordUrl(`${location.origin}/verify-discord`)
        const url = response.data.data.link

        openWindow(url, "Verify Discord")
    }

    const [isTwitterVerified, setIsTwitterVerified] = useState(false)

    const onVerifyTwitter = async () => {
        if (typeof window === "undefined") {
            return
        }

        setTwitterError("")

        const response = await getTwitterUrl(`${location.origin}/verify-twitter`)
        const url = response.data.data.link

        openWindow(url, "Verify Twitter")
    }

    const [discordError, setDiscordError] = useState("")
    useEffect(() => {
        const listenMessage = async (event) => {
            if (event.origin !== window.origin) {
                return
            }

            const data = event.data
            if (!data.error) {
                setIsDiscordVerified(true)

                await mutate(/MY_PROFILE/)
            } else {
                setDiscordError(data.message)
            }
        }

        const broadcastChannel = new BroadcastChannel("verify-discord-channel")
        broadcastChannel.onmessage = listenMessage

        return () => {
            if (broadcastChannel) {
                broadcastChannel.close()
            }
        }
    }, [])

    const [twitterError, setTwitterError] = useState("")
    useEffect(() => {
        const broadcastChannel = new BroadcastChannel("verify-twitter-channel")
        broadcastChannel.onmessage = function (event) {
            const data = event.data

            if (!data.error) {
                setIsTwitterVerified(true)

                mutate(/MY_PROFILE/)
            } else {
                setTwitterError(data.message)
            }
        }

        return () => {
            if (broadcastChannel) {
                broadcastChannel.close()
            }
        }
    }, [])

    const [isTelegramVerified, setIsTelegramVerified] = useState(false)

    const responseTelegram = async (response: TelegramUser) => {
        await verifyTelegram(response)

        setIsTelegramVerified(true)
    }

    const onSubmitHandler = async (data) => {
        if (!isLoggedIn) {
            setIsOpenLoginModal(true)

            return
        }

        setIsOpenConfirmUpdateModal(true)
    }

    const onConfirmUpdate = async () => {
        try {
            const data = getValues()

            await updateProfile(data.userName, data.photo)

            setIsOpenConfirmUpdateModal(false)

            await mutate(/MY_PROFILE/)

            onCancel()
        } catch (ex) {
            setPopupMessage({ message: ex.response ? ex.response.data.message : ex })
        }
    }

    const onCancelUpdate = async () => {
        setIsOpenConfirmUpdateModal(false)
        setIsOpenCancelConfirmUpdateModal(true)
    }

    const onCancelConfirm = () => {
        setIsOpenConfirmUpdateModal(true)
        setIsOpenCancelConfirmUpdateModal(false)
    }

    return (
        <div className={"mt-5 h-[585px] rounded-[20px] bg-[#F2F2F2] py-[20px] px-[24px] lg:h-[349px] lg:px-[34px]"}>
            {isOpenVerifyAddressModal && (
                <VerifyEmailAddressModal
                    emailAddress={watchEmail}
                    isOpen={isOpenVerifyAddressModal}
                    onResendOtp={onVerifyEmail}
                    otpCanResendAt={canResendEmailOtpAfter}
                    onRequestClose={() => setIsOpenVerifyAddressModal(false)}
                    onVerifySuccess={onVerifyEmailSuccess}
                />
            )}
            {isOpenConfirmUpdateModal && (
                <ConfirmUpdateModal
                    onConfirm={onConfirmUpdate}
                    onCancel={onCancelUpdate}
                    isOpen={isOpenConfirmUpdateModal}
                    onRequestClose={() => setIsOpenConfirmUpdateModal(false)}
                />
            )}
            {isOpenCancelConfirmUpdateModal && (
                <CancelConfirmModal
                    onCancel={onCancelConfirm}
                    isOpen={isOpenCancelConfirmUpdateModal}
                    onRequestClose={() => setIsOpenCancelConfirmUpdateModal(false)}
                />
            )}

            <form onSubmit={handleSubmit(onSubmitHandler)} className={"relative"} autoComplete={"off"}>
                <div className={"flex flex-col items-center lg:flex-row lg:items-start"}>
                    <div className={"relative"}>
                        <div
                            className={
                                "h-[113px] w-[113px] overflow-hidden rounded-full bg-[url(../assets/imgs/bg/profile.png)] bg-cover lg:h-[145px] lg:w-[145px]"
                            }
                        >
                            {preview && <img src={preview} className={"h-full w-full object-cover"} />}
                            {profileImage && <img src={profileImage} className={"h-full w-full object-cover"} />}
                        </div>
                        <div className={"absolute bottom-[-18px] left-[50%] translate-x-[-50%]"}>
                            <button>
                                <AddPhotoSvg width={37} height={37} />
                            </button>
                            <input
                                type={"file"}
                                className={"absolute top-0 left-0 h-full w-full opacity-0"}
                                onChange={onSelectFile}
                            />
                        </div>
                    </div>
                    <div className={"w-full lg:ml-[38px] lg:w-[560px]"}>
                        <div className={"mt-5 text-center text-20 font-bold leading-6 lg:mt-0 lg:text-left"}>
                            {tMyAccount("editAccount")}
                        </div>
                        <div className={"mt-[38px] flex flex-col items-center lg:mt-5 lg:flex-row"}>
                            <div
                                className={
                                    "mb-2 flex w-full min-w-[160px] items-center text-12 lg:mb-0 lg:w-auto lg:text-14 "
                                }
                            >
                                <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                    <Person1Svg width={17} height={19} className={"fill-[#546F7A]"} />
                                </div>
                                {tMyAccount("userName.label")}:
                            </div>
                            <div className={"w-full flex-1 lg:w-auto"}>
                                <input
                                    maxLength={100}
                                    placeholder={tMyAccount("userName.placeholder")}
                                    className={clsx(
                                        "h-[28px] w-full rounded-[5px] border border-[#ADADAD] indent-4 text-12 leading-4 outline-0 lg:h-[36px] lg:text-14",
                                        errors.userName && "border-[2px] border-accent-3 text-accent-3"
                                    )}
                                    {...register("userName")}
                                />
                            </div>
                        </div>
                        <div className={"mt-[9px] flex flex-col items-center lg:mt-[6px] lg:flex-row"}>
                            <div
                                className={
                                    "mb-2 flex w-full min-w-[160px] items-center text-12 lg:mb-0 lg:w-auto lg:text-14"
                                }
                            >
                                <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                    <EmailSvg width={16} height={12} className={"fill-[#546F7A]"} />
                                </div>
                                {tMyAccount("emailAddress.label")}:
                            </div>
                            <div className={"relative w-full flex-1 lg:w-auto"}>
                                {email ? (
                                    <input
                                        value={email}
                                        disabled={email}
                                        className={
                                            "h-[28px] w-full rounded-[5px] border border-[#ADADAD] indent-4 text-12 leading-4 outline-0 lg:h-[36px] lg:text-14"
                                        }
                                        {...register("email")}
                                    />
                                ) : (
                                    <input
                                        maxLength={256}
                                        placeholder={tMyAccount("emailAddress.placeholder")}
                                        className={
                                            "h-[28px] w-full rounded-[5px] border border-[#ADADAD] indent-4 text-12 leading-4 outline-0 lg:h-[36px] lg:text-14"
                                        }
                                        {...register("email")}
                                    />
                                )}
                                {isEmailVerified || email ? (
                                    <div className={"b-inset-center b-flex-center left-auto right-0"}>
                                        <CheckCircleSvg width={15} height={15} />
                                    </div>
                                ) : (
                                    <button
                                        type={"button"}
                                        onClick={onVerifyEmail}
                                        className={
                                            "b-inset-center b-flex-center left-auto right-[-10px] h-[16px] w-[39px] rounded-[2px] border border-[#F4B821] text-8 font-semibold hover:text-accent-1 lg:right-[-20px] lg:h-[24px] lg:w-[65px] lg:text-10"
                                        }
                                    >
                                        {tCommon("button.verify")}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className={"mt-[9px] flex flex-col items-center lg:mt-[6px] lg:flex-row"}>
                            <div
                                className={
                                    "mb-2 flex w-full min-w-[160px] items-center text-12 lg:mb-0 lg:w-auto lg:text-14"
                                }
                            >
                                <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                    <TwitterSvg width={18} height={14} className={"fill-[#546F7A]"} />
                                </div>
                                {tMyAccount("twitterAccount.label")}:
                            </div>
                            <div className={"relative w-full flex-1 lg:w-auto"}>
                                <input
                                    disabled
                                    value={twitter ? twitter.username : ""}
                                    placeholder={tMyAccount("twitterAccount.placeholder")}
                                    className={
                                        "h-[28px] w-full rounded-[5px] border border-[#ADADAD] indent-4 text-12 leading-4 outline-0 lg:h-[36px] lg:text-14"
                                    }
                                />
                                {isTwitterVerified || twitter ? (
                                    <div className={"b-inset-center b-flex-center left-auto right-0"}>
                                        <CheckCircleSvg width={15} height={15} />
                                    </div>
                                ) : (
                                    <button
                                        type={"button"}
                                        onClick={onVerifyTwitter}
                                        className={
                                            "b-inset-center b-flex-center left-auto right-[-10px] h-[16px] w-[39px] rounded-[2px] border border-[#F4B821] text-8 font-semibold hover:text-accent-1 lg:right-[-20px] lg:h-[24px] lg:w-[65px] lg:text-10"
                                        }
                                    >
                                        {tCommon("button.verify")}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className={"mt-[9px] flex flex-col items-center lg:mt-[6px] lg:flex-row"}>
                            <div
                                className={
                                    "mb-2 flex w-full min-w-[160px] items-center text-12 lg:mb-0 lg:w-auto lg:text-14"
                                }
                            >
                                <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                    <DiscordSvg width={17} height={19} className={"fill-[#546F7A]"} />
                                </div>
                                {tMyAccount("discordAccount.label")}:
                            </div>
                            <div className={"relative w-full flex-1 lg:w-auto"}>
                                <input
                                    disabled
                                    value={discord ? discord.username : ""}
                                    placeholder={tMyAccount("discordAccount.placeholder")}
                                    className={
                                        "h-[28px] w-full rounded-[5px] border border-[#ADADAD] indent-4 text-12 leading-4 outline-0 lg:h-[36px] lg:text-14"
                                    }
                                />
                                {isDiscordVerified || discord ? (
                                    <div className={"b-inset-center b-flex-center left-auto right-0"}>
                                        <CheckCircleSvg width={15} height={15} />
                                    </div>
                                ) : (
                                    <button
                                        type={"button"}
                                        onClick={onVerifyDiscord}
                                        className={
                                            "b-inset-center b-flex-center left-auto right-[-10px] h-[16px] w-[39px] rounded-[2px] border border-[#F4B821] text-8 font-semibold hover:text-accent-1 lg:right-[-20px] lg:h-[24px] lg:w-[65px] lg:text-10"
                                        }
                                    >
                                        {tCommon("button.verify")}
                                    </button>
                                )}
                            </div>
                        </div>
                        {/*<div className={"mt-[15px]"}>
                                <div className={"text-base font-normal leading-[23px] text-white lg:text-lg1-1"}>
                                    {tMyAccount("telegramAccount.label")}
                                </div>
                                <div className={"relative mt-3"}>
                                    <input
                                        disabled
                                        value={telegram ? telegram.username : ""}
                                        placeholder={tMyAccount("telegramAccount.placeholder")}
                                        className={
                                            "h-[40px] w-full rounded-[5px] border border-neutral-1-700 bg-neutral-1-200 pr-[100px] indent-3 font-secondary text-base leading-4 text-neutral-1-600 outline-0"
                                        }
                                    />
                                    {isTelegramVerified || telegram ? (
                                        <div
                                            className={
                                                "b-inset-center b-flex-center left-auto right-0 h-[21px] w-[21px] rounded-full bg-accent-2"
                                            }
                                        >
                                            <CheckSvg />
                                        </div>
                                    ) : (
                                        <div
                                            className={
                                                "b-inset-center b-flex-center left-auto right-[-20px] h-[24px] w-[65px] rounded-[5px] bg-accent-1 text-xs font-bold leading-[14px] text-neutral-1 hover:text-neutral-1-100"
                                            }
                                        >
                                            {tCommon("button.verify")}
                                            <TelegramButton
                                                widgetVersion={"21"}
                                                dataOnAuth={responseTelegram}
                                                buttonSize={"medium"}
                                                className={"absolute opacity-0"}
                                                botName={process.env.NEXT_PUBLIC_TELEGRAM_BOT}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>*/}
                        <div className={"mt-[38px] flex items-center justify-center lg:justify-end"}>
                            {twitterError && (
                                <div className={"mr-3 text-12 font-bold text-accent-3"}>{twitterError}</div>
                            )}
                            {discordError && (
                                <div className={"absolute font-secondary text-ssm1 text-accent-3"}>{discordError}</div>
                            )}
                            <button
                                type={"submit"}
                                className={
                                    "b-flex-center mr-4 h-[35px] w-[112px] rounded-[9px] bg-accent-1 text-base font-bold leading-6  hover:text-white"
                                }
                            >
                                {tCommon("button.update")}
                            </button>
                            <button
                                onClick={onCancel}
                                className={
                                    "b-flex-center h-[35px] w-[112px] rounded-[9px] border text-base font-bold leading-6 hover:text-accent-1"
                                }
                            >
                                {tCommon("button.cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}
